import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, XCircle, Loader2, ArrowRight, Home, AlertTriangle, Lock, Check } from "lucide-react";
import { useAuthContext } from "@/lib/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { applyActionCode, checkActionCode, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";

type ActionState = 'loading' | 'auth-loading' | 'valid' | 'success' | 'error' | 'expired' | 'invalid' | 'wrong-user' | 'redirecting';

const AuthAction = () => {
  const [actionState, setActionState] = useState<ActionState>('auth-loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [verificationEmail, setVerificationEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuthContext();
  const { toast } = useToast();
  const actionAttempted = useRef(false);
  const [totalNavHeight, setTotalNavHeight] = useState(112);

  // Get action parameters from URL
  const mode = searchParams.get('mode');
  const actionCode = searchParams.get('oobCode');

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    window.addEventListener('navHeightUpdate', handleNavHeightChange as EventListener);

    return () => {
      window.removeEventListener('navHeightUpdate', handleNavHeightChange as EventListener);
    };
  }, []);

  // Password validation function
  const validatePassword = (password: string) => {
    const requirements = {
      minLength: password.length >= 6,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };

    const errors = [];
    if (!requirements.minLength) errors.push("At least 6 characters");
    if (!requirements.hasUppercase) errors.push("One uppercase letter");
    if (!requirements.hasLowercase) errors.push("One lowercase letter");
    if (!requirements.hasNumber) errors.push("One number");
    if (!requirements.hasSpecialChar) errors.push("One special character");

    return {
      isValid: Object.values(requirements).every(Boolean),
      errors
    };
  };


  // Handle password reset verification
  const handlePasswordResetVerification = async () => {
    if (!actionCode) {
      setActionState('invalid');
      setErrorMessage('Invalid reset link. The reset code is missing.');
      return;
    }

    try {
      setActionState('loading');

      // Verify the password reset code and get the email
      const email = await verifyPasswordResetCode(auth, actionCode);
      setUserEmail(email);

      // Check if this is an admin user (should redirect to admin portal)
      const adminQuery = query(
        collection(db, 'adminUsers'),
        where('email', '==', email)
      );
      const adminSnapshot = await getDocs(adminQuery);

      if (!adminSnapshot.empty) {
        setActionState('redirecting');
        toast({
          title: "Admin Account Detected",
          description: "Redirecting to admin portal for password reset...",
        });

        // Get admin portal URL from environment variables with fallbacks
        const adminPortalBaseUrl = import.meta.env.VITE_ADMIN_PORTAL_URL || 'http://localhost:5173';

        // Construct the admin portal reset URL with the current search parameters
        const adminPortalUrl = `${adminPortalBaseUrl}/reset-password?mode=resetPassword&oobCode=${actionCode}`;
        
        // Redirect to admin portal with the reset parameters
        setTimeout(() => {
          window.location.href = adminPortalUrl;
        }, 2000); // Give user time to see the redirect message
        return;
      }

      setActionState('valid');
    } catch (error: any) {
      console.error('Password reset verification error:', error);
      
      if (error.code === 'auth/expired-action-code') {
        setActionState('expired');
        setErrorMessage('This reset link has expired. Please request a new password reset.');
      } else if (error.code === 'auth/invalid-action-code') {
        setActionState('invalid');
        setErrorMessage('This reset link is invalid or has already been used.');
      } else {
        setActionState('error');
        setErrorMessage(error.message || 'An error occurred while verifying the reset link.');
      }
    }
  };

  // Handle password reset submission
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!actionCode) {
      setErrorMessage('Invalid reset code.');
      return;
    }

    // Validate password
    const validation = validatePassword(password);
    if (!validation.isValid) {
      setPasswordError(`Password must include: ${validation.errors.join(', ')}`);
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setPasswordError('');
    setIsSubmitting(true);

    try {
      await confirmPasswordReset(auth, actionCode, password);
      
      setActionState('success');
      toast({
        title: "Password Reset Successful!",
        description: "Your password has been updated. You can now sign in with your new password.",
      });

      // Redirect to home page after a short delay
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (error: any) {
      console.error('Password reset error:', error);
      setErrorMessage(error.message || 'An error occurred while resetting your password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Main effect to handle different action modes
  useEffect(() => {
    if (actionAttempted.current) return;

    if (!mode || !actionCode) {
      setActionState('invalid');
      setErrorMessage('Invalid action link. Missing required parameters.');
      return;
    }

    // For password reset, proceed immediately
    if (mode === 'resetPassword') {
      actionAttempted.current = true;
      handlePasswordResetVerification();
    }
    // Email verification is no longer supported - redirect to home
    else if (mode === 'verifyEmail') {
      toast({
        title: "Email Verification Not Required",
        description: "Email verification is no longer required. You can access all features immediately after signing up.",
      });
      navigate('/');
      return;
    }
    // Unknown mode
    else {
      setActionState('invalid');
      setErrorMessage(`Unknown action mode: ${mode}`);
    }
  }, [mode, actionCode, authLoading, user]);

  // Helper function to render error messages with support links
  const renderErrorMessage = (message: string) => {
    const parts = message.split('contact support');
    if (parts.length === 2) {
      return (
        <>
          {parts[0]}
          <a 
            href="mailto:support@jampackd.store?subject=Authentication%20Issue&body=Hello%20JamPack'd%20Support%20Team,%0D%0A%0D%0AI%20need%20help%20with%20authentication:%0D%0A%0D%0APlease%20assist%20me%20with%20this%20issue.%0D%0A%0D%0AThank%20you!" 
            className="text-jamaica-green hover:text-jamaica-green/80 font-medium transition-colors duration-200"
          >
            contact support
          </a>
          {parts[1]}
        </>
      );
    }
    return message;
  };


  if (mode === 'resetPassword') {
    return (
      <div 
        className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
        <Card className="w-full max-w-md mx-auto shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-gray-900">
              Reset Password
            </CardTitle>
            <CardDescription className="text-gray-600">
              {actionState === 'loading' ? 'Verifying reset link...' :
               actionState === 'valid' ? 'Enter your new password' :
               actionState === 'success' ? 'Password reset successfully!' :
               actionState === 'redirecting' ? 'Redirecting to admin portal...' :
               'Password reset status'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {actionState === 'loading' && (
              <div className="flex flex-col items-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
                <p className="text-sm text-gray-600">Verifying your reset link...</p>
              </div>
            )}

            {actionState === 'redirecting' && (
              <div className="flex flex-col space-y-4">
                <Loader2 className="h-8 w-8 animate-spin text-jamaica-green mx-auto" />
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900">Admin Account Detected</h3>
                  <p className="text-sm text-gray-600">
                    We've detected that this is an admin account. You're being redirected to the admin portal to complete your password reset.
                  </p>
                  <p className="text-sm text-gray-500">
                    If you're not redirected automatically, please{" "}
                    <a 
                      href="mailto:support@jampackd.store?subject=Admin%20Portal%20Redirect%20Issue&body=Hello%20JamPack'd%20Support%20Team,%0D%0A%0D%0AI%20was%20not%20automatically%20redirected%20to%20the%20admin%20portal%20for%20password%20reset:%0D%0A%0D%0AEmail:%20{userEmail}%0D%0A%0D%0APlease%20assist%20me%20with%20this%20issue.%0D%0A%0D%0AThank%20you!" 
                      className="text-jamaica-green hover:text-jamaica-green/80 font-medium transition-colors duration-200"
                    >
                      contact support
                    </a>
                  </p>
                </div>
              </div>
            )}

            {actionState === 'valid' && (
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={userEmail}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                    New Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your new password"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                    Confirm New Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new password"
                    required
                  />
                </div>

                {passwordError && (
                  <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                    {passwordError}
                  </div>
                )}

                <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md">
                  <p className="font-medium mb-1">Password requirements:</p>
                  <ul className="space-y-1">
                    <li className="flex items-center">
                      <Check className={`w-3 h-3 mr-2 ${password.length >= 6 ? 'text-green-600' : 'text-gray-400'}`} />
                      At least 6 characters
                    </li>
                    <li className="flex items-center">
                      <Check className={`w-3 h-3 mr-2 ${/[A-Z]/.test(password) ? 'text-green-600' : 'text-gray-400'}`} />
                      One uppercase letter
                    </li>
                    <li className="flex items-center">
                      <Check className={`w-3 h-3 mr-2 ${/[a-z]/.test(password) ? 'text-green-600' : 'text-gray-400'}`} />
                      One lowercase letter
                    </li>
                    <li className="flex items-center">
                      <Check className={`w-3 h-3 mr-2 ${/\d/.test(password) ? 'text-green-600' : 'text-gray-400'}`} />
                      One number
                    </li>
                    <li className="flex items-center">
                      <Check className={`w-3 h-3 mr-2 ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? 'text-green-600' : 'text-gray-400'}`} />
                      One special character
                    </li>
                  </ul>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Resetting Password...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Reset Password
                    </>
                  )}
                </Button>
              </form>
            )}

            {actionState === 'success' && (
              <div className="flex flex-col space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900">Password Reset Successful!</h3>
                  <p className="text-sm text-gray-600">
                    Your password has been successfully updated. You can now sign in with your new password.
                  </p>
                  <p className="text-xs text-gray-500">
                    Redirecting to home page in a few seconds...
                  </p>
                </div>
                <Button 
                  onClick={() => navigate('/')}
                  className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go to Home
                </Button>
              </div>
            )}

            {(actionState === 'error' || actionState === 'expired' || actionState === 'invalid') && (
              <div className="flex flex-col space-y-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <XCircle className="h-8 w-8 text-red-600" />
                </div>
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {actionState === 'expired' ? 'Link Expired' :
                     actionState === 'invalid' ? 'Invalid Link' : 'Reset Failed'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {renderErrorMessage(errorMessage)}
                  </p>
                  {actionState === 'expired' && (
                    <p className="text-sm text-gray-500">
                      Please request a new password reset email.
                    </p>
                  )}
                </div>
                <div className="flex flex-col w-full space-y-2">
                  <Button 
                    onClick={() => navigate('/')}
                    className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Go to Home
                  </Button>
                </div>
              </div>
            )}

            {errorMessage && actionState !== 'success' && actionState !== 'redirecting' && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                {renderErrorMessage(errorMessage)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Unknown mode
  return (
    <div 
      className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100"
      style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
    >
      <Card className="w-full max-w-md mx-auto shadow-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-gray-900">
            Invalid Action
          </CardTitle>
          <CardDescription className="text-gray-600">
            Unknown authentication action
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">Invalid Action</h3>
              <p className="text-sm text-gray-600">
                The authentication action "{mode}" is not recognized. Please check your link or contact support if you continue to have issues.
              </p>
            </div>
            <Button 
              onClick={() => navigate('/')}
              className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white"
            >
              <Home className="w-4 h-4 mr-2" />
              Go to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthAction; 